import { useTranslation } from 'react-i18next';
import Modal from '@/components/ui/Modal';
import { formatPrice } from '@/components/ui/Price';
import { useCatalogText } from '@/lib/localizeCatalog';
import { selectedCount, toggleCartOption } from '@/features/checkout/cartOptions';
import type { OptionGroup } from '@/types/menu';
import type { SelectedOption } from '@/types/cart';

interface Props {
  open: boolean;
  /** Les cadeaux que le panier a débloqués : ils se choisissent ici. */
  unlocked: OptionGroup[];
  /** Les paliers suivants, du plus proche au plus lointain. */
  locked: { group: OptionGroup; gapCents: number }[];
  selections: SelectedOption[];
  onChange: (selection: SelectedOption) => void;
  /** Referme le panier et renvoie le client au menu. */
  onAddMore: () => void;
  onClose: () => void;
}

/**
 * SC-1438 — « Mes cadeaux » : ce que le restaurant offre, palier par palier, et
 * le choix du cadeau débloqué sans attendre la page de paiement.
 *
 * Le bandeau rouge du panier ne dit que ce qui manque pour le palier suivant ;
 * il ne montrait ni ce qu'on y gagne exactement, ni ce qu'on a déjà gagné. Le
 * choix fait ici est la même réponse que celle de l'étape « Avant de valider »
 * (`checkout.cartOptions`) : la page de paiement la retrouve cochée.
 */
export default function CartGiftsModal({
  open,
  unlocked,
  locked,
  selections,
  onChange,
  onAddMore,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const ct = useCatalogText();
  const euros = (cents: number) => (cents / 100).toFixed(2).replace('.', ',');

  return (
    <Modal open={open} onClose={onClose} ariaLabel={t('cart.gifts.title')}>
      <div
        className="px-4 py-3 border-b-2 border-ink flex items-center justify-between"
        data-testid="cart-gifts-modal"
      >
        <h2 className="font-display text-xl m-0">🎁 {t('cart.gifts.title')}</h2>
        <button
          type="button"
          onClick={onClose}
          className="bg-hot-pink text-[var(--on-accent)] border-2 border-ink rounded-btn font-display text-xl leading-none w-8 h-8 flex items-center justify-center"
          aria-label={t('cart.close')}
        >×</button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-4">
        {unlocked.map((group) => {
          const selection = selections.find((s) => s.groupId === group.id);
          const max = group.max ?? 1;
          const multiple = max > 1;
          const full = multiple && selectedCount(selection) >= max;
          return (
            <section key={group.id} data-testid={`cart-gift-unlocked-${group.id}`}>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-display text-base">{ct.name(group)}</span>
                <span className="rounded-btn-sm border-2 border-solid border-ink bg-mint px-1.5 py-[1px] font-mono text-[11px] font-bold text-[var(--on-primary)]">
                  ✓ {t('cart.gifts.unlocked')}
                </span>
                {multiple && (
                  <span className="font-mono text-xs text-ink/60">
                    {t('checkout.cartOptions.upTo', { max })}
                  </span>
                )}
              </div>
              <ul
                className="mt-2 list-none p-0 m-0 flex flex-col gap-2"
                role={multiple ? undefined : 'radiogroup'}
                aria-label={ct.name(group)}
              >
                {group.options.map((opt) => {
                  const checked = (selection?.options ?? []).some((o) => o.id === opt.id);
                  const blocked = !checked && full;
                  return (
                    <li key={opt.id}>
                      <button
                        type="button"
                        role={multiple ? 'checkbox' : 'radio'}
                        aria-checked={checked}
                        disabled={blocked}
                        onClick={() => {
                          const next = toggleCartOption(group, selection, opt.id);
                          if (next) onChange(next);
                        }}
                        data-testid={`cart-gift-option-${opt.id}`}
                        className={`flex w-full items-center gap-2 border-2 border-solid px-2.5 py-2.5 text-start font-mono text-sm transition-colors ${
                          checked ? 'border-ink bg-cream shadow-stamp-sm' : 'border-ink bg-paper'
                        } ${blocked ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}
                      >
                        <span
                          aria-hidden="true"
                          className={`flex h-5 w-5 flex-none items-center justify-center border-2 border-solid border-ink ${
                            multiple ? '' : 'rounded-full'
                          } ${checked ? 'bg-ink' : 'bg-paper'}`}
                        >
                          {checked && (
                            <span className={`block h-2 w-2 bg-paper ${multiple ? '' : 'rounded-full'}`} />
                          )}
                        </span>
                        {opt.photo && (
                          <img
                            src={opt.photo}
                            alt=""
                            loading="lazy"
                            className="h-10 w-10 flex-none border border-solid border-ink/20 object-cover"
                          />
                        )}
                        <span className="min-w-0 flex-1 break-words">{ct.name(opt)}</span>
                        {opt.priceDelta > 0 && (
                          <span className="flex-none font-mono text-xs font-bold text-ink">
                            + {formatPrice(opt.priceDelta)}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
        {locked.map(({ group, gapCents }) => (
          <section key={group.id} data-testid={`cart-gift-locked-${group.id}`}>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="font-display text-base">🔒 {ct.name(group)}</span>
              <span
                className="rounded-btn-sm border-2 border-solid border-ink px-1.5 py-[1px] font-mono text-[11px] font-bold text-white"
                style={{ background: 'var(--sale-red)' }}
              >
                {t('cart.upsell.missing', { amount: euros(gapCents) })}
              </span>
            </div>
            <ul className="mt-2 list-none p-0 m-0 flex flex-wrap gap-1.5">
              {group.options.map((opt) => (
                <li
                  key={opt.id}
                  className="flex items-center gap-1.5 border-2 border-dashed border-ink/30 px-2 py-1 font-mono text-xs text-ink/70"
                >
                  {opt.photo && (
                    <img src={opt.photo} alt="" loading="lazy" className="h-6 w-6 flex-none object-cover" />
                  )}
                  {ct.name(opt)}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
      <div className="border-t-2 border-ink p-4 bg-cream flex justify-end gap-2">
        {locked.length > 0 && (
          <button
            type="button"
            onClick={onAddMore}
            className="bg-paper border-2 border-ink rounded-btn px-3 py-2 font-display text-sm"
            data-testid="cart-gifts-add-more"
          >
            {t('cart.upsell.addMore')}
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="bg-mint text-[var(--on-primary)] border-2 border-ink rounded-btn px-5 py-2 font-display shadow-stamp-sm"
          data-testid="cart-gifts-done"
        >
          {t('cart.gifts.done')}
        </button>
      </div>
    </Modal>
  );
}
